import { useState, useEffect } from 'react';
import { reportsApi, ReportResponse, CompanyReportData } from '../../api/reports.api';
import { branchesApi, Branch } from '../../api/branches.api';
import { companiesApi, Company } from '../../api/companies.api';
import Button from '../../components/forms/Button';
import Select from '../../components/forms/Select';
import Loading from '../../components/common/Loading';
import CompanyReport from './components/CompanyReport';
import { generateCompanyReportPDF } from '../../utils/pdfGenerator';

const CompanyReportPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ReportResponse<CompanyReportData> | null>(null);

  const [branches, setBranches] = useState<Branch[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);

  const [companyId, setCompanyId] = useState<string>('all');
  const [branchId, setBranchId] = useState<string>('all');
  const [status, setStatus] = useState<string>('all');

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const [branchesData, companiesData] = await Promise.all([
        branchesApi.getAll(),
        companiesApi.getAll(),
      ]);
      setBranches(branchesData.filter(b => b.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
      setCompanies(companiesData.filter(c => c.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
    } catch (err: any) {
      console.error('Filtre seçenekleri yüklenirken hata:', err);
    }
  };

  const handleGenerateReport = async () => {
    setIsLoading(true);
    setError(null);
    setReportData(null);

    try {
      const filters: any = {};
      if (companyId && companyId !== 'all') filters.companyId = companyId;
      if (branchId && branchId !== 'all') filters.branchId = branchId;
      if (status && status !== 'all') filters.status = status;

      const data = await reportsApi.getCompanyReport(filters);
      setReportData(data);
    } catch (err: any) {
      setError(err.message || 'Rapor oluşturulurken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setCompanyId('all');
    setBranchId('all');
    setStatus('all');
    setReportData(null);
  };

  const handleExportPDF = () => {
    if (reportData) {
      generateCompanyReportPDF(reportData);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Şirket Bazlı Cihaz Raporu</h1>
          <p className="text-sm text-gray-600 mt-1">Şirketlere göre cihaz dağılımını görüntüleyin</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtreler</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Şirket"
            value={companyId}
            onChange={(value) => setCompanyId(value)}
            options={[
              { value: 'all', label: 'Tüm Şirketler' },
              ...companies.map(c => ({ value: c.id, label: c.name }))
            ]}
            searchable={true}
          />

          <Select
            label="Şube"
            value={branchId}
            onChange={(value) => setBranchId(value)}
            options={[
              { value: 'all', label: 'Tüm Şubeler' },
              ...branches.map(b => ({ value: b.id, label: b.name }))
            ]}
            searchable={true}
          />

          <Select
            label="Durum"
            value={status}
            onChange={(value) => setStatus(value)}
            options={[
              { value: 'all', label: 'Tüm Durumlar' },
              { value: 'Available', label: 'Şubede' },
              { value: 'Assigned', label: 'Zimmetli' },
              { value: 'InRepair', label: 'Onarımda' },
              { value: 'Retired', label: 'Kullanım Dışı' },
              { value: 'Scrapped', label: 'Hurda' },
            ]}
            searchable={true}
          />
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleGenerateReport} disabled={isLoading}>
            {isLoading ? 'Rapor Oluşturuluyor...' : 'Rapor Oluştur'}
          </Button>
          <Button variant="outline" onClick={handleClearFilters}>
            Filtreleri Temizle
          </Button>
          {reportData && (
            <Button variant="secondary" onClick={handleExportPDF}>
              PDF Olarak Kaydet
            </Button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && <Loading text="Rapor oluşturuluyor..." />}

      {/* Report Display */}
      {reportData && !isLoading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <CompanyReport data={reportData} />
        </div>
      )}
    </div>
  );
};

export default CompanyReportPage;
